import { IControl, IDropDownControl, IDataControl } from "Controls/Interfaces";
import { IDisposable } from "Core/Interfaces";
import { IDataRow, IGridCommandOption, IODataResult } from "Entities";
import { EditModes } from "Enums";
import { DropDownService, TextBlockService, CommandService, PeoplePickerService, DatabaseImageService } from "Service";
import { EventArgs } from "Core/Events";
import { AppContext, ApplicationParameter } from "Core";
import { ControlEventTypes } from "Core/Events/EventTypes";
import { DatabaseImage, PeoplePicker, UserPicker } from "Controls";
import { BaseGridDefinition, DataRequestOption, GridTypes, GridControlTypes } from "Service/Client";
import CancellationToken from "cancellationtoken";

/**
 * The EditContext stores all edit-controls for a Grid.
 * In Grids the EditContext is 1 edited row, in Forms the EditContext is the whole form.
 * It handles the loading the data of comboboxes, dependent controls and gridcommands.
 * @hidden
 */
export class EditContext implements IDisposable {
    private controls: Array<IControl>;
    private controlDict: { [controlID: number]: IControl };
    private dataRowFunction: () => IDataRow;
    private afterValidateFunction: (isValid: boolean, firstInvalidControl: IControl) => void;
    private textBlockCategory: string;

    // control which was registered as default button
    private defaultButtonControl: IControl;

    private dependentControls: { [controlID: number]: IControl[] };

    // store all controls for each radiogroup.
    private radioButtonControlIds: { [radioGroupName: string]: number[] };

    // store all controls which need to be updated after execution of gridCommand with gridCommandID
    private dependentGridCommandControls: { [gridCommandID: number]: IControl[] };

    // store all controls which were already registered on gridcommand change event.
    private registeredGridCommandControls: { [controlId: number]: boolean } = {};

    // stores all control ids which have their value assigned to applicationparameter.
    private applicationParameterControlIds: number[] = [];

    private _isInitialized: boolean;

    public editMode: EditModes;
    private gridDefinition: BaseGridDefinition;

    private validator: kendo.ui.Validator;

    private comboboxService: DropDownService;
    private commandService: CommandService;
    private textBlockService: TextBlockService;
    private databaseImageService: DatabaseImageService;

    private rootDropDownControls: IControl[];
    private loadedDropDownControls: { [controlId: number]: boolean };
    private controlDataChangedDropDownHandler: any;

    private dropDownCancellationTokenSource: {
        token: CancellationToken;
        cancel: (reason?: any) => void;
    };

    /**
     * Create the editContext.
     * @param gridDefinition
     */
    constructor(
        gridDefinition: BaseGridDefinition,
        comboboxService: DropDownService,
        commandService: CommandService,
        textBlockService: TextBlockService,
        databaseImageService: DatabaseImageService
    ) {
        this.gridDefinition = gridDefinition;
        this.comboboxService = comboboxService;
        this.commandService = commandService;
        this.textBlockService = textBlockService;
        this.databaseImageService = databaseImageService;
        this.controlDataChangedDropDownHandler = this.onControlChangedDropDownDataHandler.bind(this);
    }

    /**
     * Handler occurs when the control value changes to load dependent control data.
     * @param e EventArgs.
     */
    private onControlChangedDropDownDataHandler(e: EventArgs<IControl>) {
        this.onControlChangedDropDownData(e.sender);
    }

    /**
     * Check if editContext was initialized.
     */
    public get isInitialized(): boolean {
        return this._isInitialized;
    }

    /**
     * Registers an editable control for this editContext.
     * @param control
     */
    public registerControl(control: IControl): void {
        if (!this.isInitialized) {
            return;
        }

        this.controls.push(control);
        this.controlDict[control.getControlId()] = control;

        let controlDefinition = control.getControlDefinition();

        // register defaultButton
        if (controlDefinition.IsDefaultButton) {
            this.defaultButtonControl = control;
        }

        // control value should be added to applicationparameters.
        if (controlDefinition.ApplicationParameter) {
            this.applicationParameterControlIds.push(control.getControlId());
        }

        // control is part of radiogroup.
        if (controlDefinition.RadioGroup) {
            if (this.radioButtonControlIds[controlDefinition.RadioGroup] == null) {
                this.radioButtonControlIds[controlDefinition.RadioGroup] = [];
            }
            // remember all controls for this radiogroup.
            this.radioButtonControlIds[controlDefinition.RadioGroup].push(controlDefinition.ControlID);
        }

        // check comboboxcontrols which are dependent of the current control.
        let comboboxControl = <IDropDownControl>control;
        if (comboboxControl.getDependencyControlIds) {
            let dependencyControls = comboboxControl.getDependencyControlIds();
            if (dependencyControls && dependencyControls.length > 0) {
                for (let depId = 0, depLength = dependencyControls.length; depId < depLength; depId++) {
                    let depControlId = dependencyControls[depId];
                    if (this.dependentControls[depControlId] == null) {
                        this.dependentControls[depControlId] = [];
                    }
                    this.dependentControls[depControlId].push(control);
                }
            } else {
                this.rootDropDownControls.push(control);
            }
        }

        // register all controls which are dependent from gridCommand
        if (controlDefinition.GridCommandID) {
            let gridCommandID = controlDefinition.GridCommandID;

            if (this.dependentGridCommandControls[gridCommandID] == null) {
                this.dependentGridCommandControls[gridCommandID] = [];
            }
            this.dependentGridCommandControls[gridCommandID].push(control);
        }

        // register handlers on controls
        control.on(ControlEventTypes.GetDataRow, () => this.onGetDataRow());
        if (controlDefinition.CheckRequiredFields || controlDefinition.CommandID) {
            control.on(ControlEventTypes.CheckRequiredFields, (e) => this.onCheckRequiredFields(e));
        }
        control.on(ControlEventTypes.BeforeExecuteContext, (e) => this.onBeforeExecute(e));
        control.on(ControlEventTypes.AfterExecuteContext, (e) => this.onAfterExecute(e));
        if (controlDefinition.RadioGroup) {
            control.on(ControlEventTypes.RadioButtonChange, (e) => this.onRadioButtonChange(e));
        }
        control.on(ControlEventTypes.ControlReloadRequested, (e) => this.onControlReloadRequested(e));
    }

    /**
     * Occurs when a control requests to reload its data.
     * @param e EventArgs.
     */
    private onControlReloadRequested(e: EventArgs<IControl>): Promise<void> {
        let control = e.sender;
        this.setDropDownControlsBusy(control, true, true);

        return this.loadControlData([control], true);
    }

    /**
     * Handler when a control value is changed which is registered for applicationparameter.
     * @param control Control which value was changed.
     */
    private onApplicationParameterControlChanged(control: IControl) {
        let value = control.getValue();
        // handle date values which are not from date controls (i.e dates in combobox values)
        if (value != null && typeof value === "string") {
            let dateValue = kendo.parseDate(value.toString());
            if (dateValue) {
                value = dateValue.toISOString();
            }
        }
        ApplicationParameter.set(control.getControlDefinition().ApplicationParameter, value);
    }

    /**
     * Handler which delivers the current dataItem when requested from a Control.
     */
    private onGetDataRow(): IDataRow {
        return this.getDataRow();
    }

    /**
     * Validates a control if the given input matches the given control.
     * @param input Input-Element from DOM to be checked.
     * @param control Control to be checked.
     */
    private validateControl(input: JQuery, control: IControl): boolean {
        if (input[0] == control.getOuterElement()[0]) {
            if (control.getControlDefinition().RadioGroup && !control.isEnabled()) {
                return true;
            }
            return this.isControlValueSet(control);
        }
        return true;
    }

    private async loadControlDataRecursive(dropDownControls: IControl[], loadRoot: boolean = true): Promise<void> {
        if (loadRoot) {
            await this.loadControlData(dropDownControls);
        }
        let controlsToLoad: IControl[] = [];

        // set all loaded comboboxes to loadedCOmboboxes
        for (let cbId = 0, cbLen = dropDownControls.length; cbId < cbLen; cbId++) {
            let combobox = dropDownControls[cbId];

            this.loadedDropDownControls[combobox.getControlId()] = true;
        }

        for (let cbId = 0, cbLen = dropDownControls.length; cbId < cbLen; cbId++) {
            let dropDownControl = dropDownControls[cbId];

            let dependentControls = this.dependentControls[dropDownControl.getControlDefinition().ControlID] || [];
            for (let depControl of dependentControls) {
                let load = true;

                for (let parentControl of depControl.getControlDefinition().DependencyControlIds || []) {
                    if (this.loadedDropDownControls[parentControl] === false) {
                        load = false;
                        break;
                    }
                }

                if (load) {
                    if (controlsToLoad.indexOf(depControl) == -1) {
                        controlsToLoad.push(depControl);
                    }
                }
            }
        }

        if (controlsToLoad.length) {
            await this.loadControlDataRecursive(controlsToLoad);
        }
    }

    /**
     * Check required fields on all controls of this EditContext.
     */
    public checkRequiredFields(): { isValid: boolean; firstInvalidControl: IControl } {
        let isValid = true;
        let firstInvalidControl: IControl;

        for (let i = 0, len = this.controls.length; i < len; i++) {
            let control = this.controls[i];
            let ctrDef = control.getControlDefinition();
            let fieldRequired =
                (this.editMode == EditModes.Edit && ctrDef.EditRequiredText && ctrDef.OnEditEditable) ||
                (this.editMode == EditModes.Add && ctrDef.AddRequiredText && ctrDef.OnAddEditable);

            if (fieldRequired) {
                // call the kendo validator for each required control.
                // the validator shows the validation message at every control.
                let validationResult = this.validator.validateInput(control.getOuterElement());

                if (!validationResult) {
                    // set the width for validation messages in grid so they dont overlap to next column.
                    if (this.gridDefinition.GridTypeID == GridTypes.Grid || this.gridDefinition.GridType == GridTypes.EasyEdit) {
                        let outerElement = control.getOuterElement();
                        outerElement.next(".k-invalid-msg").outerWidth(outerElement.outerWidth());
                    }
                    isValid = false;
                    if (!firstInvalidControl) {
                        firstInvalidControl = control;
                    }
                }
            }
        }
        return {
            isValid: isValid,
            firstInvalidControl: firstInvalidControl,
        };
    }

    /**
     * Handler is called when a Control (mostly {ButtonControl}) requests to check the {EditContext} for required fields.
     * @param e EventArgs
     */
    private onCheckRequiredFields(e: EventArgs<IControl>): void {
        let result = this.checkRequiredFields();
        if (!result.isValid) {
            e.sender.playAnimation("shake");
            e.preventDefault();
        }
        if (this.afterValidateFunction) {
            this.afterValidateFunction(result.isValid, result.firstInvalidControl);
        }
    }

    /**
     * Handler is called when a control (mostly {ButtonControl}) requests to execute beforeExecuteScripts of all controls in this context.
     * @param e EventArgs
     */
    private onBeforeExecute(e: EventArgs<IControl>): void {
        // loop through all controls
        for (let i = 0, len = this.controls.length; i < len; i++) {
            let ctrl = this.controls[i];

            // trigger the beforeExecuteScript on the control.
            let eventArgs = new EventArgs<EditContext>(this);
            ctrl.trigger(ControlEventTypes.BeforeExecuteControl, eventArgs);

            // event is stopped, as soon as the first control requested to stop execution (returned false in script)
            if (e.isDefaultPrevented()) {
                return;
            }
        }
    }

    /**
     * Handler is called when a control (mostly {ButtonControl}) requests to execute afterExecuteScripts of all controls in this context.
     * @param e EventArgs
     */
    private onAfterExecute(e: EventArgs<IControl>): void {
        // loop through all controls
        for (let i = 0, len = this.controls.length; i < len; i++) {
            let ctrl = this.controls[i];

            // trigger the afterExecuteScript on the control.
            let eventArgs = new EventArgs<EditContext>(this);
            ctrl.trigger(ControlEventTypes.AfterExecuteControl, eventArgs);
            // event is stopped, as soon as the first control requested to stop execution (returned false in script)
            if (e.isDefaultPrevented()) {
                return;
            }
        }
    }

    /**
     * Checks if the control has a value.
     * @param ctr Control to check.
     */
    private isControlValueSet(ctr: IControl): boolean {
        let val = ctr.getValue();
        return val !== null && val !== "";
    }

    /**
     * Gets the current DataRow.
     */
    public getDataRow(): IDataRow {
        return this.dataRowFunction && this.dataRowFunction();
    }

    /**
     * Sets the function which the EditContext can use to retrieve the current IDataRow.
     * @param dataRowFunc Function which returns current IDataRow.
     */
    public setDataRowFunction(dataRowFunction: () => IDataRow): void {
        this.dataRowFunction = dataRowFunction;
    }

    /**
     * Sets the function which the EditContext calls after validating required fields.
     * @param afterValidateFunction Function which will be called after validation.
     */
    public setOnAfterValidateFunction(afterValidateFunction: (isValid: boolean, firstInvalidControl: IControl) => void): void {
        this.afterValidateFunction = afterValidateFunction;
    }

    /**
     * Initialize the EditContext
     * @param editMode Current EditMode
     */
    public initialize(editMode: EditModes): void {
        this.editMode = editMode;
        this.controls = new Array<IControl>();
        this.controlDict = {};
        this.dependentControls = {};
        this.dependentGridCommandControls = {};
        this.radioButtonControlIds = {};
        this.registeredGridCommandControls = {};
        this.rootDropDownControls = [];
        this.loadedDropDownControls = {};
        this._isInitialized = true;
        this.dropDownCancellationTokenSource = CancellationToken.create();
    }

    /**
     * Trigger the default button (if exists) in this {EditContext}.
     * Returns true if the default button was triggered.
     */
    public triggerDefaultButton(): boolean {
        if (this.defaultButtonControl) {
            this.defaultButtonControl.trigger(ControlEventTypes.Click);
            return true;
        } else {
            return false;
        }
    }

    /**
     * Load EditContext:
     * - Register all change handlers for dependent controls.
     * - Initialize validation.
     * - Initialize radioButtons.
     * - Load control data.
     * - Load initial GridCommands.
     * - Register keypress (only enter) for defaultButton.
     * @param container Container of controls.
     */
    public async load(currentTextBlockCategory: string, container: JQuery, model?: kendo.data.Model): Promise<void[]> {
        this.textBlockCategory = currentTextBlockCategory;
        if (!this.isInitialized) {
            return;
        }

        const prevDirty = (model && model.dirty) || false;

        // register keypress event for input, select, textarea
        if (container) {
            container.find("input, select, button").keyup((e) => {
                if (e.which == 13) {
                    //Enter key
                    $(e.target).blur(); // blur on sender element to update bindings before triggering.
                    this.triggerDefaultButton();
                    $(e.target).focus();
                }
            });
        }

        // register the change handler for all dependent controls of comboboxes.
        let keys: string[];

        // initialize radio button controls.
        keys = Object.keys(this.radioButtonControlIds);
        for (let i = 0, cnt = keys.length; i < cnt; i++) {
            let radioGroupControls = this.radioButtonControlIds[keys[i]];

            // get first control of each radio group and set to
            if (radioGroupControls.length > 0) {
                let firstRadioGroupControl = this.controlDict[radioGroupControls[0]];
                if (firstRadioGroupControl) {
                    firstRadioGroupControl.setRadioButtonChecked(true);
                }
            }
        }

        // initialize controls which get their value assigned in application applicationparameters
        for (let controlId of this.applicationParameterControlIds) {
            let control = this.controlDict[controlId];
            control.on(ControlEventTypes.Change, () => this.onApplicationParameterControlChanged(control));
            this.onApplicationParameterControlChanged(control);
        }

        // initialize the control validation
        this.initializeValidation();

        // check all dependency controls which are not in rootDropDownControls
        // if the control is dependent of non-comboboxcontrols (i.e. number) -> add to rootDropDownControls to load initially
        for (const control of this.controls) {
            let comboboxControl = <IDropDownControl>control;
            if (comboboxControl.getDependencyControlIds && this.rootDropDownControls.indexOf(comboboxControl) == -1) {
                let dependencyControls = comboboxControl.getDependencyControlIds();
                let isRoot = true;
                for (const depId of dependencyControls) {
                    let depCtrl = <IDropDownControl>this.controlDict[depId];
                    if (depCtrl && depCtrl.getDependencyControlIds) {
                        isRoot = false;
                        break;
                    }
                }
                if (isRoot) {
                    this.rootDropDownControls.push(comboboxControl);
                }
            }
        }

        // load data for all controls
        let controlsToLoad = this.rootDropDownControls || [];

        for (const control of this.controls) {
            let ctrl = <IDropDownControl>control;
            if (!ctrl.getDataRequest && ctrl.setData && ctrl.getControlDefinition().CommandID != null) {
                controlsToLoad.push(control);
            }
        }

        for (const control of controlsToLoad) {
            this.setDropDownControlsBusy(control, true);
        }

        await this.loadControlDataRecursive(this.rootDropDownControls);

        // register the change handler for all dependent controls in gridCommands.
        if (this.gridDefinition.GridCommands) {
            keys = Object.keys(this.gridDefinition.GridCommands);
            for (let i = 0, cnt = keys.length; i < cnt; i++) {
                let gridCommand = this.gridDefinition.GridCommands[Number(keys[i])];

                for (let cmdIdx = 0, cmdCnt = gridCommand.InputControlIDs.length; cmdIdx < cmdCnt; cmdIdx++) {
                    let ctrId = gridCommand.InputControlIDs[cmdIdx];

                    if (this.registeredGridCommandControls[ctrId]) {
                        continue;
                    }

                    let ctr = this.controlDict[ctrId];
                    if (ctr != null) {
                        ctr.on(ControlEventTypes.Change, (e) => this.onControlChangedGridCommand(e.sender));
                    }
                    this.registeredGridCommandControls[ctrId] = true;
                }
            }
        }

        if (this.dependentControls) {
            keys = Object.keys(this.dependentControls);
            for (let i = 0, cnt = keys.length; i < cnt; i++) {
                let ctr = this.controlDict[Number(keys[i])];
                if (ctr != null) {
                    ctr.on(ControlEventTypes.Change, this.controlDataChangedDropDownHandler);
                }
            }
        }

        // load all grid commands
        await this.loadGridCommands();

        // reset dirty flag of model after all data is loaded
        // register change handler for dirty flag
        if (model) {
            model.unbind("change", this.changeDirtyFlagHandler);

            if (!prevDirty) {
                model.dirty = false;
                (<any>model).dirtyFields = {};
            }
            model.bind("change", this.changeDirtyFlagHandler);
        }

        // trigger LoadScripts-Events of all controls
        this.triggerLoadScripts();

        await this.loadImages(<IDataRow>(<any>model));
    }

    private async loadImages(dataRow: IDataRow) {
        if (!this.controls) {
            return;
        }

        DatabaseImage.loadMultiple(this.controls, this.databaseImageService, this.gridDefinition.GridID, dataRow);
    }

    private changeDirtyFlagHandler(e: kendo.data.ObservableObjectEvent) {
        const model = <kendo.data.Model>(<any>e.sender).source;
        if (model && model.dirty && (<any>model)[e.field] == model.get(`__original.${e.field}`)) {
            const dirtyFields = (<any>model).dirtyFields;
            delete dirtyFields[e.field];
            if (Object.keys(dirtyFields).length === 0) {
                model.dirty = false;
            }
        }
    }

    /**
     * Triggers LoadScript-Events of all controls
     */
    private triggerLoadScripts() {
        if (!this.controls) {
            return;
        }

        for (let i = 0, len = this.controls.length; i < len; i++) {
            this.controls[i].trigger(ControlEventTypes.ControlLoaded);
        }
    }

    /**
     * Creates the validator with custom rules.
     */
    private initializeValidation(): void {
        let validatorRules: { [ruleName: string]: (input: JQuery) => boolean } = {};
        let validatorMessages: { [ruleName: string]: string } = {};

        for (let i = 0, len = this.controls.length; i < len; i++) {
            let control = this.controls[i];
            let ctrDef = control.getControlDefinition();

            let fieldRequired =
                (this.editMode == EditModes.Edit && ctrDef.EditRequiredText && ctrDef.OnEditEditable) ||
                (this.editMode == EditModes.Add && ctrDef.AddRequiredText && ctrDef.OnAddEditable);

            if (fieldRequired) {
                let fieldRequiredText = this.editMode == EditModes.Edit ? ctrDef.EditRequiredText : ctrDef.AddRequiredText;
                let ruleName = "dms-rule-" + ctrDef.ControlID;
                validatorRules[ruleName] = (input: JQuery) => this.validateControl(input, control);
                validatorMessages[ruleName] = this.textBlockService.get(fieldRequiredText, this.textBlockCategory);
            }
        }

        let validatorOptions: kendo.ui.ValidatorOptions = {
            rules: validatorRules,
            messages: validatorMessages,
            validationSummary: false,
        };

        if (this.gridDefinition.GridTypeID == GridTypes.Grid || this.gridDefinition.GridTypeID == GridTypes.EasyEdit) {
            validatorOptions = $.extend(validatorOptions, <kendo.ui.ValidatorOptions>{
                errorTemplate: `<div class="k-tooltip k-tooltip-error k-validator-tooltip k-invalid-msg"><span class="far fa-circle-exclamation k-tooltip-icon"></span><span class="k-tooltip-content">#=message#</span><span class="k-callout k-callout-n"></span></div>`,
            });
        } else {
            validatorOptions = $.extend(validatorOptions, <kendo.ui.ValidatorOptions>{
                errorTemplate: `<div class="k-tooltip k-tooltip-error k-validator-tooltip k-invalid-msg" style="position:relative"><span class="far fa-circle-exclamation k-tooltip-icon"></span><span class="k-tooltip-content">#=message#</span><span class="k-callout k-callout-n"></span></div>`,
            });
        }
        this.validator = new kendo.ui.Validator($("<div/>")[0], validatorOptions);
        // overwrite _extractMessage of the kendo validator, because they use the tooltip of the control before the rule-message as errormessage.
        (<any>this.validator)._extractMessage = function (input: JQuery, ruleKey: string): string {
            return (<kendo.ui.Validator>this).options.messages[ruleKey];
        };
    }

    /**
     * Handler is raised when a control is changed which is a parameter of a gridCommand.
     * @param ctr Control which raised the changed-event.
     */
    private onControlChangedGridCommand(ctr: IControl) {
        this.loadGridCommands(ctr);
    }

    /**
     * Loads GridCommands and sets the result values.
     * @param ctr (optional)Changed Control.
     */
    private async loadGridCommands(ctr?: IControl): Promise<void> {
        if (!this.gridDefinition.GridCommands) {
            return;
        }

        let options: IGridCommandOption[] = [];
        let dataRow = this.getDataRow();

        let gridCommandIds = Object.keys(this.gridDefinition.GridCommands);
        for (let cmdId = 0, cmdLen = gridCommandIds.length; cmdId < cmdLen; cmdId++) {
            let gridCommand = this.gridDefinition.GridCommands[Number(gridCommandIds[cmdId])];

            // if the gridcommand is loaded by a changed control, check if control is input parameter of the gridCommand
            // otherwise all gridCommands are looped.
            if (ctr == null || gridCommand.InputControlIDs.indexOf(ctr.getControlDefinition().ControlID) != -1) {
                // check if all controls for this command have values set.
                let hasValue: boolean = true;
                for (let ctrIdx = 0, ctrLen = gridCommand.InputControlIDs.length; ctrIdx < ctrLen; ctrIdx++) {
                    let inputControl = this.controlDict[gridCommand.InputControlIDs[ctrIdx]];

                    if (inputControl != null) {
                        hasValue = this.isControlValueSet(inputControl);
                        if (!hasValue) {
                            // no value set, break loop
                            break;
                        }
                    } else {
                        hasValue = false;
                        break;
                    }
                }

                if (hasValue) {
                    // if all values are set, add the command to the command list.
                    options.push({
                        GridCommandID: gridCommand.GridCommandID,
                        DependentValueCommandID: gridCommand.DependentValueCommandID,
                    });
                }
            }
        }

        if (options.length > 0) {
            // send request to the server
            let data = await this.commandService.gridCommand(this.gridDefinition.GridID, options, dataRow);
            let gridCommandIds = Object.keys(data);

            for (let cmdId = 0, cmdLen = gridCommandIds.length; cmdId < cmdLen; cmdId++) {
                let gridCommandId = Number(gridCommandIds[cmdId]);
                let gridCommandData = data[gridCommandId];

                // values to controls
                this.setGridCommandData(gridCommandData, this.dependentGridCommandControls[gridCommandId]);
            }
        }
    }

    /**
     * Set IODataResult of the GridCommand to the Controls.
     * @param data Result of the executed GridCommand.
     * @param controls List of registered Controls.
     */
    private setGridCommandData(data: IODataResult, controls: IControl[]): void {
        if (data != null && data.value != null && data.value.length == 1) {
            let dataRow = <IDataRow>data.value[0]; // take first row

            if (controls) {
                for (let i = 0, len = controls.length; i < len; i++) {
                    let ctrl = controls[i];

                    let value = dataRow[ctrl.getControlDefinition().DependentValueColumnName];
                    ctrl.setValue(value);

                    // grid command changed the value of a control -> reload possible dependent dropdowns
                    this.onControlChangedDropDownData(ctrl);
                }
            }
        }
    }

    /**
     * Handler is raised when a dependent control is changed.
     * @param ctr Changed control.
     */
    private onControlChangedDropDownData(ctr: IControl) {
        let depControls = this.dependentControls[ctr.getControlId()];

        if (depControls && depControls.length > 0) {
            let keys = Object.keys(this.dependentControls);
            for (let i = 0, cnt = keys.length; i < cnt; i++) {
                let ctr = this.controlDict[Number(keys[i])];
                if (ctr != null) {
                    ctr.off(ControlEventTypes.Change, this.controlDataChangedDropDownHandler);
                }
            }
            this.resetLoadedDropDownControls(ctr);
            this.setDropDownControlsBusy(ctr, true, false);
            this.loadControlDataRecursive([ctr], false).then(() => {
                keys = Object.keys(this.dependentControls);
                for (let i = 0, cnt = keys.length; i < cnt; i++) {
                    let ctr = this.controlDict[Number(keys[i])];
                    if (ctr != null) {
                        ctr.on(ControlEventTypes.Change, this.controlDataChangedDropDownHandler);
                    }
                }
            });
        }
    }

    /**
     * Reset the load state of the drop down controls.
     * @param ctr The root control.
     */
    private resetLoadedDropDownControls(ctr: IControl) {
        this.loadedDropDownControls[ctr.getControlId()] = false;

        let depControls = this.dependentControls[ctr.getControlId()];
        if (depControls && depControls.length > 0) {
            for (let depControl of depControls) {
                this.resetLoadedDropDownControls(depControl);
            }
        }
    }

    /**
     * Set the busy status of drop down controls and all dependent drop down controls.
     * @param ctr Root control.
     * @param isBusy Busy status.
     * @param setForRoot Wether to set the busy status for the root control.
     */
    private setDropDownControlsBusy(ctr: IControl, isBusy: boolean, setForRoot: boolean = true) {
        let control = <IDropDownControl>ctr;
        if (control.setBusy && setForRoot) {
            control.setBusy(isBusy);
        }

        let depControls = this.dependentControls[ctr.getControlId()];
        if (depControls && depControls.length > 0) {
            for (let depControl of depControls) {
                this.setDropDownControlsBusy(depControl, isBusy);
            }
        }
    }

    /**
     * Loads data for given controls.
     * Those are either all controls in this EditContext for initial loading
     * or dependent controls on control change.
     * @param ctr
     */
    public async loadControlData(ctr: IControl[], reloadFromDataSource: boolean = false): Promise<void> {
        let options: DataRequestOption[] = [];
        let peoplePickerOptions: DataRequestOption[] = [];
        let userPickerOptions: DataRequestOption[] = [];

        let dataControls: IDataControl[] = [];
        let peoplePickers: PeoplePicker[] = [];
        let userPickers: UserPicker[] = [];
        let dataRow = this.getDataRow();

        for (let i = 0, len = ctr.length; i < len; i++) {
            let comboboxControl = <IDropDownControl>ctr[i];
            if (comboboxControl.getControlType() == GridControlTypes.PeoplePicker) {
                let peoplePicker = <PeoplePicker>comboboxControl;
                peoplePicker.setBusy(true);
                let ctlInfo: DataRequestOption = comboboxControl.getDataRequest();
                if (ctlInfo != null) {
                    peoplePickerOptions.push(ctlInfo);
                }
                peoplePickers.push(peoplePicker);
            } else if (comboboxControl.getControlType() == GridControlTypes.UserPicker) {
                let userPicker = <UserPicker>comboboxControl;
                userPicker.setBusy(true);
                let ctlInfo: DataRequestOption = comboboxControl.getDataRequest();
                if (ctlInfo != null) {
                    userPickerOptions.push(ctlInfo);
                }
                userPickers.push(userPicker);
            } else if (comboboxControl.getDataRequest) {
                let ctlInfo: DataRequestOption = comboboxControl.getDataRequest();
                if (ctlInfo != null) {
                    ctlInfo.ReloadFromDataSource = reloadFromDataSource;
                    options.push(ctlInfo);
                }
            } else {
                let dataControl = <IDataControl>ctr[i];
                if (dataControl.setData && dataControl.getControlDefinition().CommandID != null) {
                    dataControls.push(dataControl);
                }
            }
        }

        if (options.length > 0) {
            // request data from service
            let data = await this.comboboxService.getAll(
                this.gridDefinition.GridID,
                options,
                dataRow,
                this.dropDownCancellationTokenSource.token
            );
            let setDataPromises: Promise<void>[] = [];

            if (data) {
                for (const option of options) {
                    const controlId = option.ControlID;
                    let ctrlData = data[controlId];
                    if (ctrlData != null && !ctrlData.error && this.controlDict && this.controlDict[controlId]) {
                        let ctl = this.controlDict[controlId];
                        let combobox = <IDropDownControl>ctl;
                        if (combobox.setData) {
                            setDataPromises.push(combobox.setData(ctrlData));
                        }
                    }
                }
            }

            await Promise.all(setDataPromises);
        }

        for (let i = 0, len = dataControls.length; i < len; i++) {
            let dataControl = dataControls[i];
            dataControl.setBusy(true);
            this.commandService
                .getControl(this.gridDefinition.GridID, dataControl.getControlDefinition().ControlID, dataRow)
                .then((result) => {
                    dataControl.setData(result);
                    dataControl.setBusy(false);
                });
        }

        if (peoplePickers.length > 0) {
            let peoplePickerService = new PeoplePickerService();

            let data = await peoplePickerService.getAll(this.gridDefinition.GridID, peoplePickerOptions, dataRow);
            let setDataPromises: Promise<void>[] = [];

            for (let i = 0, len = peoplePickerOptions.length; i < len; i++) {
                let ctrlData = data[peoplePickerOptions[i].ControlID];
                if (ctrlData != null) {
                    if (!ctrlData.error) {
                        let ctl = this.controlDict[peoplePickerOptions[i].ControlID];
                        let combobox = <IDropDownControl>ctl;
                        if (combobox.setData) {
                            setDataPromises.push(combobox.setData(ctrlData));
                        }
                    }
                }
            }
            await Promise.all(setDataPromises);
        }

        if (userPickers.length > 0) {
            let userPickerService = AppContext.current.serviceProvider.User;

            let data = await userPickerService.getAll(this.gridDefinition.GridID, userPickerOptions, dataRow);
            let setDataPromises: Promise<void>[] = [];

            for (let i = 0, len = userPickerOptions.length; i < len; i++) {
                let ctrlData = data[userPickerOptions[i].ControlID];
                if (ctrlData != null) {
                    if (!ctrlData.error) {
                        let ctl = this.controlDict[userPickerOptions[i].ControlID];
                        let combobox = <IDropDownControl>ctl;
                        if (combobox.setData) {
                            setDataPromises.push(combobox.setData(ctrlData));
                        }
                    }
                }
            }
            await Promise.all(setDataPromises);
        }
    }

    /**
     * Find control in EditContext.
     * @param controlId ControlId to find.
     */
    public findControl(control: number | HTMLElement): IControl {
        if (!this.isInitialized) {
            return null;
        }

        if (typeof control === "number") {
            return this.controlDict[control];
        } else {
            for (let i = 0, len = this.controls.length; i < len; i++) {
                if (this.controls[i].getElement() == control) {
                    return this.controls[i];
                }
            }
        }
        return null;
    }

    /**
     * Eventhandler is executed when a radio button of a control was clicked.
     * @param ctr Control which raised the event.
     */
    public onRadioButtonChange(e: EventArgs<IControl>): void {
        let control = e.sender;
        let controlDefinition = control.getControlDefinition();

        let radioButtonIds = this.radioButtonControlIds[controlDefinition.RadioGroup];
        for (let i = 0, len = radioButtonIds.length; i < len; i++) {
            if (radioButtonIds[i] != controlDefinition.ControlID) {
                let otherControl = this.controlDict[radioButtonIds[i]];
                // clear value and disable all other control of this radiogroup.
                otherControl.setValue(null);
                otherControl.disable();
            }
        }
        // enable the sender, which was the clicked element.
        control.enable();
    }

    /**
     * Dispose the EditContext and all registered Controls.
     */
    public dispose(): void {
        for (let i = 0, len = this.controls.length; i < len; i++) {
            this.controls[i].dispose();
        }
        this.controls = null;
        this.controlDict = null;
        this.dependentControls = null;
        this.dependentGridCommandControls = null;
        this.radioButtonControlIds = null;
        this.dataRowFunction = null;
        this.afterValidateFunction = null;
        this.registeredGridCommandControls = null;
        this._isInitialized = false;
        this.dropDownCancellationTokenSource.cancel();
    }
}
